// src/pages/financeiro/PagamentoRecebimentoModal.js
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/modalForm/StackedBootstrapModal';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import { useFormaPag } from '../../contexts/financeiro/FormaPagContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { formatCurrency, formatNumeric, normalizeDecimalInputBr, parseNumeric } from '../../utils/numberUtils';
import { formatDate } from '../../utils/dateTimeUtils';
import { registrarPagamentoRecebimento } from '../../services/financeiro/RecebimentoService';
import { distribuirPagamentoPorVencimento } from '../../models/financeiro/Recebimento';

const agoraParaInput = () => {
    const agora = new Date();
    agora.setMinutes(agora.getMinutes() - agora.getTimezoneOffset());
    return agora.toISOString().slice(0, 16);
};

const PagamentoRecebimentoModal = ({ show, recebimento, parcelas, onHide, onSuccess }) => {
    const { formasPag } = useFormaPag();
    const { showMessage } = useMessage();
    const [formaPagId, setFormaPagId] = useState('');
    const [valorPagamento, setValorPagamento] = useState('');
    const [dataPagamento, setDataPagamento] = useState(agoraParaInput());
    const [salvando, setSalvando] = useState(false);

    const saldoSelecionado = useMemo(() => parcelas.reduce(
        (total, parcela) => total + Number(parcela.saldo || 0), 0
    ), [parcelas]);
    const valorPagamentoNumerico = useMemo(() => parseNumeric(valorPagamento), [valorPagamento]);
    const distribuicao = useMemo(
        () => distribuirPagamentoPorVencimento(parcelas, valorPagamentoNumerico),
        [parcelas, valorPagamentoNumerico]
    );

    useEffect(() => {
        if (!show) return;
        setValorPagamento(formatNumeric(saldoSelecionado));
        setDataPagamento(agoraParaInput());
        const primeiraAtiva = formasPag.find((forma) => !forma.inativo);
        setFormaPagId(primeiraAtiva?.id ? String(primeiraAtiva.id) : '');
        setSalvando(false);
    }, [show, saldoSelecionado, formasPag]);

    const pagar = async () => {
        const valor = valorPagamentoNumerico;
        if (!formaPagId) return showMessage('Informe a forma de pagamento.', 'warning');
        if (!dataPagamento) return showMessage('Informe a data do pagamento.', 'warning');
        if (!valor || valor <= 0) return showMessage('Informe um valor maior que zero.', 'warning');
        if (valor > saldoSelecionado + 0.001) {
            return showMessage('O valor não pode ser maior que o saldo selecionado.', 'warning');
        }

        setSalvando(true);
        try {
            const resultado = await registrarPagamentoRecebimento({
                recebimentoId: recebimento.id,
                parcelaIds: parcelas.map((parcela) => parcela.id),
                valorPagamento: valor,
                formaPagId: Number(formaPagId),
                dataPagamento: `${dataPagamento}:00`,
            });
            await onSuccess?.(resultado.recebimento);
            onHide();
            showMessage('Pagamento registrado com sucesso!', 'success');
        } catch (error) {
            showMessage(error?.message || 'Não foi possível registrar o pagamento.', 'danger');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal show={show} onHide={salvando ? undefined : onHide} centered size="lg" className="pagamento-modal">
            <Modal.Header closeButton={!salvando}>
                <div>
                    <Modal.Title>Registrar pagamento</Modal.Title>
                    <small className="text-muted">Recebimento #{recebimento?.id} · {recebimento?.clienteNome}</small>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="pagamento-resumo mb-3">
                    <div><span>Parcelas</span><strong>{parcelas.length}</strong></div>
                    <div><span>Saldo selecionado</span><strong>{formatCurrency(saldoSelecionado)}</strong></div>
                    <div><span>Valor informado</span><strong>{formatCurrency(valorPagamentoNumerico)}</strong></div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-5">
                        <Select label="Forma de pagamento" value={formaPagId} size="100%"
                            onChange={(event) => setFormaPagId(event.target.value)} disabled={salvando}
                            options={[
                                { value: '', label: 'Selecione' },
                                ...formasPag.filter((forma) => !forma.inativo)
                                    .map((forma) => ({ value: forma.id, label: forma.descricao })),
                            ]} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Valor" type="text" value={valorPagamento} size="100%"
                            style={{ textAlign: 'right' }}
                            onChange={(event) => setValorPagamento(normalizeDecimalInputBr(event.target.value))}
                            onBlur={() => setValorPagamento(formatNumeric(valorPagamento))}
                            disabled={salvando} />
                    </div>
                    <div className="col-md-4">
                        <Input label="Data e hora" type="datetime-local" value={dataPagamento} size="100%"
                            onChange={(event) => setDataPagamento(event.target.value)} disabled={salvando} />
                    </div>
                </div>

                <div className="distribuicao-pagamento">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Distribuição por vencimento</strong>
                        <small className="text-muted">A parcela mais antiga recebe primeiro</small>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead><tr><th>Parcela</th><th>Vencimento</th><th className="text-end">Saldo</th><th className="text-end">Aplicar</th></tr></thead>
                            <tbody>
                                {distribuicao.map((parcela) => (
                                    <tr key={parcela.id}>
                                        <td>{parcela.numParcela}</td>
                                        <td>{formatDate(parcela.dataVencimento)}</td>
                                        <td className="text-end">{formatCurrency(parcela.saldo)}</td>
                                        <td className="text-end fw-bold text-success">{formatCurrency(parcela.valorAplicado)}</td>
                                    </tr>
                                ))}
                                {!distribuicao.length && <tr><td colSpan="4" className="text-center text-muted">Informe o valor para visualizar a distribuição.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline-secondary" onClick={onHide} disabled={salvando}>Cancelar</button>
                <button type="button" className="btn btn-success px-4" onClick={pagar} disabled={salvando}>
                    {salvando ? 'Registrando...' : 'Confirmar pagamento'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default PagamentoRecebimentoModal;
